import React, { useEffect, useState } from 'react';
import { motion, useAnimationControls } from 'framer-motion';

const DataQualityAnimation = () => {
  const controls = useAnimationControls();
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  useEffect(() => {
    if (!isMounted) return;
    
    const runAnimation = async () => {
      try {
        await controls.start("reset");
        await controls.start("animate");
        await new Promise(resolve => setTimeout(resolve, 5000));
        if (isMounted) {
          runAnimation();
        }
      } catch (error) {
        console.error("Animation error:", error);
      }
    };
    
    runAnimation();
  }, [isMounted, controls]);

  return (
    <div className="relative w-full overflow-hidden">
      <motion.div 
        className="bg-[#EDE9FE] rounded-xl p-4 sm:p-6 relative h-[500px] sm:h-[400px] flex flex-col sm:flex-row items-center transform-gpu"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        {/* Container that changes from column (mobile) to row (desktop) */}
        <div className="flex flex-col sm:flex-row items-center w-full justify-center scale-[0.9] sm:scale-90 origin-top sm:origin-center">
          {/* Shield Icon */}
          <div className="relative mb-2 sm:mb-0 sm:mr-4 flex flex-col items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={controls}
              variants={{
                reset: { opacity: 0, scale: 0.8 },
                animate: { opacity: 1, scale: 1, transition: { delay: 0.3 } }
              }}
              className="flex flex-col items-center"
            >
              <div className="w-12 h-12 rounded-full bg-indigo-600 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <span className="text-xs font-medium text-gray-700 text-center mt-1">Data<br/>Quality</span>
            </motion.div>
          </div>

          {/* Main Dashboard Container */}
          <motion.div 
            className="bg-white rounded-lg shadow-lg w-full max-w-5xl overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={controls}
            variants={{
              reset: { opacity: 0, y: 20 },
              animate: { opacity: 1, y: 0, transition: { delay: 1 } }
            }}
          >
            {/* Dashboard Header */}
            <div className="bg-purple-600 p-3 flex items-center">
              <div className="flex space-x-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
              </div>
              <span className="text-white text-sm ml-3">Data Quality Monitoring</span>
            </div>

            {/* Dashboard Content */}
            <div className="p-3 sm:p-4 space-y-3">
              {/* Quality Score Overview */}
              <motion.div
                className="bg-gray-50 rounded-lg p-3"
                initial={{ opacity: 0 }}
                animate={controls}
                variants={{
                  reset: { opacity: 0 },
                  animate: { opacity: 1, transition: { delay: 1.5 } }
                }}
              >
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-sm font-medium text-gray-700">Overall Quality Score</h3>
                  <motion.div
                    className="text-lg font-bold text-indigo-600"
                    initial={{ opacity: 0 }}
                    animate={controls}
                    variants={{
                      reset: { opacity: 0 },
                      animate: { opacity: 1, transition: { delay: 1.7 } }
                    }}
                  >
                    92%
                  </motion.div>
                </div>
                <motion.div
                  className="h-2 bg-indigo-100 rounded-full overflow-hidden"
                  initial={{ width: 0 }}
                  animate={controls}
                  variants={{
                    reset: { width: 0 },
                    animate: { width: "100%", transition: { delay: 1.8 } }
                  }}
                >
                  <motion.div 
                    className="h-full bg-indigo-600 rounded-full"
                    initial={{ width: 0 }}
                    animate={controls}
                    variants={{
                      reset: { width: 0 },
                      animate: { width: "92%", transition: { delay: 2, duration: 1 } }
                    }}
                  />
                </motion.div>
              </motion.div>

              {/* Quality Tests Grid */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { 
                    name: 'Customer Data', 
                    status: 'Passed', 
                    tests: '12/12',
                    type: 'success'
                  },
                  { 
                    name: 'Sales Data', 
                    status: 'Warning', 
                    tests: '8/10',
                    type: 'warning'
                  },
                ].map((dataset, i) => (
                  <motion.div
                    key={i}
                    className="bg-gray-50 rounded-lg p-3"
                    initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }}
                    animate={controls}
                    variants={{
                      reset: { opacity: 0, x: i % 2 === 0 ? -20 : 20 },
                      animate: { opacity: 1, x: 0, transition: { delay: 2.2 + (i * 0.2) } }
                    }}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-sm font-medium text-gray-700">{dataset.name}</span>
                      <motion.div
                        className={`px-2 py-1 rounded-full text-xs ${
                          dataset.type === 'success' 
                            ? 'bg-green-100 text-green-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}
                        initial={{ scale: 0 }}
                        animate={controls}
                        variants={{
                          reset: { scale: 0 },
                          animate: { scale: 1, transition: { delay: 2.4 + (i * 0.2) } }
                        }}
                      >
                        {dataset.status}
                      </motion.div>
                    </div>
                    <div className="text-xs text-gray-500 mb-1">Tests Passed: {dataset.tests}</div>
                    <motion.div
                      className={`h-1.5 ${dataset.type === 'success' ? 'bg-green-100' : 'bg-yellow-100'} rounded-full overflow-hidden`}
                      initial={{ width: 0 }}
                      animate={controls}
                      variants={{
                        reset: { width: 0 },
                        animate: { width: "100%", transition: { delay: 2.6 + (i * 0.2) } }
                      }}
                    >
                      <div 
                        className={`h-full ${dataset.type === 'success' ? 'bg-green-500' : 'bg-yellow-500'} rounded-full`}
                        style={{ width: dataset.type === 'success' ? '100%' : '80%' }}
                      />
                    </motion.div>
                  </motion.div>
                ))}
              </div>

              {/* Recent Alerts */}
              <motion.div
                className="bg-gray-50 rounded-lg p-3"
                initial={{ opacity: 0, y: 20 }}
                animate={controls}
                variants={{
                  reset: { opacity: 0, y: 20 },
                  animate: { opacity: 1, y: 0, transition: { delay: 3 } }
                }}
              >
                <h3 className="text-sm font-medium text-gray-700 mb-2">Recent Alerts</h3>
                <div className="space-y-2">
                  {[
                    {
                      text: 'Schema change detected in Marketing dataset',
                      icon: (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      ),
                      time: '10 min ago'
                    },
                    {
                      text: 'Data volume anomaly in Sales Pipeline',
                      icon: (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      ),
                      time: '45 min ago'
                    },
                    {
                      text: 'All quality tests passed for Customer Data',
                      icon: (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      ),
                      time: '2 hours ago'
                    }
                  ].map((alert, i) => (
                    <motion.div
                      key={i}
                      className="text-xs text-gray-600 flex items-center justify-between"
                      initial={{ opacity: 0, x: -10 }}
                      animate={controls}
                      variants={{
                        reset: { opacity: 0, x: -10 },
                        animate: { opacity: 1, x: 0, transition: { delay: 3.2 + (i * 0.2) } }
                      }}
                    >
                      <div className="flex items-center">
                        <div className="w-5 h-5 bg-white rounded-full shadow-sm flex items-center justify-center mr-2">
                          {alert.icon}
                        </div>
                        {alert.text}
                      </div>
                      <span className="text-gray-400 text-xs">{alert.time}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Processing Indicator */}
        <motion.div
          className="absolute bottom-2 right-2 flex space-x-1"
          animate={{
            opacity: [0.3, 1, 0.3],
          }}
          transition={{
            repeat: Infinity,
            duration: 1.5,
            ease: "linear"
          }}
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-1 h-1 bg-purple-500 rounded-full"
              style={{
                animationDelay: `${i * 0.2}s`
              }}
            />
          ))}
        </motion.div>
      </motion.div>
    </div>
  );
};

export default DataQualityAnimation; 